import { Circle, Delete, ExpandMore, Save } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    TextField,
    Tooltip,
    Switch,
    IconButton,
    styled,
    useTheme,
    Box,
} from "@mui/material";
import { ElementType, useState, useRef, useCallback} from "react";
import { InstructionContent} from "./InstructionContent";
import {
    HelperInstruction as InstructionData,
} from "@my-helpers/common";

const DescriptionField = styled(TextField)(({ error, focused }) => ({
    flexGrow: 1,
    paddingRight: '16px',
    '& .MuiInput-input': {
        fontSize: '0.9rem',
        fontWeight: 600,
        color: 'text.primary',
    },
    '& .MuiInput-root:before': {
        borderBottom: !error && !focused ? 'none' : undefined,
    },
    '& .MuiInput-root:hover:before': {
        borderBottom: !error && !focused ? 'none !important' : undefined,
    },
    '& .MuiFormHelperText-root': {
        display: 'none',
    },
    '&:focus-within .MuiFormHelperText-root': {
        display: 'block',
        fontSize: '0.65rem', // Reduce font size (default is usually 0.75rem)
        lineHeight: 1.2,
        marginTop: '3px', // Reduce top margin if needed
    }
}));

interface HelperInstructionProps {
    helperId?: string;
    instruction?: InstructionData;
    isDefaultInstruction: boolean;
    createInstruction: (helperId: string, instruction: InstructionData) => Promise<boolean>;
    deleteInstruction: (helperId: string, instructionId: string) => Promise<boolean>;
    updateInstruction: (helperId: string, instructionId: string, instruction: InstructionData) => Promise<boolean>;
    handleDefaultInstructionChange: (value: string) => void;
    setShowAddForm: (value: boolean) => void;
}

export const HelperInstruction: ElementType<HelperInstructionProps> = ({
    helperId,
    instruction,
    isDefaultInstruction,
    handleDefaultInstructionChange,
    createInstruction,
    deleteInstruction,
    updateInstruction,
    setShowAddForm
}) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState<boolean>(!instruction);

    const [instructionForm, setInstructionForm] = useState<InstructionData>({
        description: instruction?.description || '',
        content: instruction?.content || '',
        enabled: instruction?.enabled || true,
        is_default: instruction?.is_default || false
    });

    const descriptionRef = useRef<HTMLInputElement>(null);

    const getDescriptionHelperText = () => {
        if (instructionForm.description.length === 0) {
            return "required";
        }
        if (descriptionRef.current === document.activeElement && instructionForm.description.length > 0) {
            return `${instructionForm.description.length}/255 characters`;
        }
        return ' '; // empty string 
    };

    const isInstructionValid = () => {
        return !!(
            instructionForm.description.trim() &&
            instructionForm.description.length <= 255 &&
            instructionForm.content.trim()
        );
    };

    const handleInputChange = useCallback((field: keyof InstructionData) => (value: any) => {
        setInstructionForm({
            ...instructionForm,
            [field]: value
        });
    }, [instructionForm]);

    const handleSubmit = useCallback(async (action: 'create' | 'update' | 'delete') => {
        
        if (!isInstructionValid()) {
            return;
        }
        if (helperId && instruction && instruction.id && action === 'update') {
            await updateInstruction(helperId, instruction.id, instructionForm);
        }
        if (helperId && instruction && instruction.id && action === 'delete') {
            await deleteInstruction(helperId, instruction.id);
        }
        if (helperId && !instruction && action === 'create') {
            await createInstruction(helperId, instructionForm);
            setShowAddForm(false);
        }
    }, [isInstructionValid, helperId, instruction, instructionForm, setShowAddForm]);

    const handleEnableToggle = useCallback(async () => {
        if (helperId && instruction && instruction.id) {
            setInstructionForm({
                ...instructionForm,
                enabled: !instructionForm.enabled
            });
            await updateInstruction(helperId, instruction.id, {
                ...instruction,
                enabled: !instruction.enabled
            });
        }
    }, [helperId, instruction, instructionForm]);

    return (
        <Accordion
            disableGutters
            key={instruction?.id}
            expanded={expanded}
            onChange={() => {}}
            sx={{
                boxShadow: 'none',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                '&:before': {
                    display: 'none',
                },
            }}>
            <AccordionSummary
                expandIcon={
                    <ExpandMore onClick={(e) => {
                        e.stopPropagation(); // prevent summary click
                        setExpanded(!expanded)
                    }} />
                }
                sx={{
                    '& .MuiAccordionSummary-content': {
                        margin: "0.5rem 0",
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'default' // Remove pointer cursor from content
                    },
                    '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: "0.5rem 0",
                    },
                    '&:focus-within': {
                        backgroundColor: 'inherit !important', // stop summary to get dark when focused
                    }
                }}
            >
                <DescriptionField
                    value={instructionForm.description}
                    required
                    error={instructionForm.description.length === 0}
                    onChange={(e) => {                       
                        handleInputChange('description')(e.target.value)
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSubmit('update');
                            (e.target as HTMLInputElement).blur();
                        }
                    }}
                    variant="standard"
                    placeholder="Enter instruction description..."
                    inputProps={{
                        maxLength: 255,
                    }}
                    helperText={getDescriptionHelperText()}
                />
                <Box component="div">
                    {
                        instruction ? (
                            <>
                                <Tooltip title="Set default instruction">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (instruction && instruction.id) {
                                                handleDefaultInstructionChange(instruction.id)
                                            }
                                        }}
                                        sx={{
                                            mr: "0.4rem",
                                            width: '1rem',
                                            height: '1rem',
                                            backgroundColor: isDefaultInstruction ? theme.palette.primary.main : '#d1d5db',
                                            color: isDefaultInstruction ? theme.palette.primary.main : '#d1d5db',
                                            '&:hover': {
                                                backgroundColor: isDefaultInstruction ? theme.palette.primary.dark : '#9ca3af',
                                                color: isDefaultInstruction ? theme.palette.primary.dark : '#9ca3af',
                                            }
                                        }}
                                    >
                                        <Circle />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip
                                    title={instruction.enabled ? "Disable instruction" : "Enable instruction"}
                                >
                                    <Switch
                                        name="enabled"
                                        checked={instruction.enabled}
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEnableToggle();
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title="Delete instruction"
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => handleSubmit('delete')}
                                        sx={{
                                            color: 'text.secondary',
                                            '&:hover': {
                                                color: 'error.main'
                                            }
                                        }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) :
                        <Tooltip title="Save instruction">
                            <IconButton
                                size="small"
                                disabled={!isInstructionValid}
                                onClick={async (e) =>{
                                    e.stopPropagation();
                                    handleSubmit('create');
                                }}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        color: 'primary.main'
                                    }
                                }}
                            >
                                <Save fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    }
                </Box>
            </AccordionSummary>
            <AccordionDetails
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2
                }}
            >
                <InstructionContent
                    id="content-editor"
                    value={instructionForm.content}
                    onChange={handleInputChange('content')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                            handleSubmit('update');
                            (e.target as HTMLInputElement).blur();
                        }
                    }}
                />
            </AccordionDetails>
        </Accordion>
    );
}